import clsx from "clsx";
import CheckBox from "../CheckBox/CheckBox";
import { MdCheck, MdClose, MdLightbulb } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import Modal from "../Modal/Modal";
import PushSubscription from "../PushSubscription/PushSubscription";
import useCurrentUser from "src/hooks/useCurrentUser";
import useCache from "src/hooks/useCache";
import apiClient from "src/helpers/api-client";
import { objectHasChanges, showNotification } from "src/helpers/utils";
import ModalHasUnsavedChanges from "../Modal/ModalHasUnsavedChanges";
import { API_ENDPOINT_UPDATE_PROFILE } from "src/helpers/endpoint-urls";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

interface INotificationSettings {
  onClose?: () => void;
  showSubscriptionMessage?: boolean;
}

interface INotificationsOnChange {
  onChange: (data: any) => void;
  columns?: number;
}

export enum EPushNotifications {
  OVERDUE_TRANSACTIONS = "OVERDUE_TRANSACTIONS",
  UPCOMING_TRANSACTIONS = "UPCOMING_TRANSACTIONS",
  TODAY_TRANSACTIONS = "TODAY_TRANSACTIONS",
  PENDING_TRANSACTIONS = "PENDING_TRANSACTIONS",
  CONTACT_REQUESTS = "CONTACT_REQUESTS",
}

const PushNotificationsSettings: React.FC<INotificationsOnChange> = ({
  onChange,
  columns = 1,
}) => {
  const { settings } = useCurrentUser();
  const { t } = useTranslation(["modals", "common"]);
  const [overdueTransactionsActive, setOverfueTransactionsActive] =
    useState<boolean>(
      !!settings?.notifications?.pushNotifications?.includes?.(
        EPushNotifications.OVERDUE_TRANSACTIONS,
      ),
    );
  const [upcomingTransactionsActive, setUpcomingTransactionsActive] =
    useState<boolean>(
      !!settings?.notifications?.pushNotifications?.includes?.(
        EPushNotifications.UPCOMING_TRANSACTIONS,
      ),
    );
  const [todayTransactionsActive, setTodayTransactionsActive] =
    useState<boolean>(
      !!settings?.notifications?.pushNotifications?.includes?.(
        EPushNotifications.TODAY_TRANSACTIONS,
      ),
    );
  const [pendingTransactionsActive, setPendingTransactionsActive] =
    useState<boolean>(
      !!settings?.notifications?.pushNotifications?.includes?.(
        EPushNotifications.PENDING_TRANSACTIONS,
      ),
    );
  const [contactRequestsActive, setContactRequestsActive] = useState<boolean>(
    !!settings?.notifications?.pushNotifications?.includes?.(
      EPushNotifications.CONTACT_REQUESTS,
    ),
  );

  useEffect(() => {
    onChange({
      overdueTransactionsActive,
      upcomingTransactionsActive,
      todayTransactionsActive,
      pendingTransactionsActive,
      contactRequestsActive,
    });
  }, [
    overdueTransactionsActive,
    upcomingTransactionsActive,
    todayTransactionsActive,
    pendingTransactionsActive,
    contactRequestsActive,
  ]);

  return (
    <div className={clsx(["flex", "w-full", "flex-col", "gap-4", "md:gap-6"])}>
      <div className={clsx(["flex", "w-full", "md:text-sm", "text-sm"])}>
        {t("modals:notifications.push.collapsible.description")}
      </div>
      <div
        className={clsx([
          "grid",
          "grid-cols-1",
          `md:grid-cols-${columns}`,
          "w-full",
          "gap-1",
        ])}
      >
        <CheckBox
          checked={overdueTransactionsActive}
          onChange={(isChecked: boolean) =>
            setOverfueTransactionsActive(isChecked)
          }
          labelClass="!font-light md:!text-sm"
          label={t(
            "modals:notifications.push.collapsible.overdue_transactions",
          )}
        />
        <CheckBox
          checked={upcomingTransactionsActive}
          onChange={(isChecked: boolean) =>
            setUpcomingTransactionsActive(isChecked)
          }
          labelClass="!font-light md:!text-sm"
          label={t(
            "modals:notifications.push.collapsible.upcoming_transactions",
          )}
        />
        <CheckBox
          checked={todayTransactionsActive}
          onChange={(isChecked: boolean) =>
            setTodayTransactionsActive(isChecked)
          }
          labelClass="!font-light md:!text-sm"
          label={t("modals:notifications.push.collapsible.today_transactions")}
        />
        <CheckBox
          checked={pendingTransactionsActive}
          onChange={(isChecked: boolean) =>
            setPendingTransactionsActive(isChecked)
          }
          labelClass="!font-light md:!text-sm"
          label={t(
            "modals:notifications.push.collapsible.pending_transactions",
          )}
        />
        <CheckBox
          checked={contactRequestsActive}
          onChange={(isChecked: boolean) => setContactRequestsActive(isChecked)}
          labelClass="!font-light md:!text-sm"
          label={t("modals:notifications.push.collapsible.contact_requests")}
        />
      </div>
    </div>
  );
};

const NotificationSettings: React.FC<INotificationSettings> = ({
  onClose,
  showSubscriptionMessage = false,
}) => {
  const { t } = useTranslation();
  const { settings } = useCurrentUser();
  const [isUpdatingSettings, setIsUpdatingSettings] = useState<boolean>(false);
  const { clearCache } = useCache();
  const [pushNotificationSettings, setPushNotificationSettings] =
    useState<any>(null);
  const [showConfirmSaveDialog, setShowConfirmSaveDialog] =
    useState<boolean>(false);

  const updateProfileSettings = async () => {
    setIsUpdatingSettings(true);

    const formData = new FormData();

    formData.append(
      "settings",
      JSON.stringify({
        ...settings,
        notifications: {
          ...(settings?.notifications || {}),
          pushNotifications: selectedNotifications,
        },
      }),
    );

    return apiClient
      .put(API_ENDPOINT_UPDATE_PROFILE, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(async (res: any) => {
        await clearCache();

        onClose?.();

        showNotification({
          kind: "success",
          message: t("modals:notifications.notifications.success"),
        });
        setShowConfirmSaveDialog(false);
      })
      .catch((e) => {
        showNotification({
          message: t("modals:notifications.notifications.error"),
          kind: "error",
        });
      })
      .finally(() => {
        setIsUpdatingSettings(false);
      });
  };

  const selectedNotifications = useMemo(() => {
    let pushNotifications = [];

    if (pushNotificationSettings?.contactRequestsActive) {
      pushNotifications.push(EPushNotifications.CONTACT_REQUESTS);
    }
    if (pushNotificationSettings?.overdueTransactionsActive) {
      pushNotifications.push(EPushNotifications.OVERDUE_TRANSACTIONS);
    }

    if (pushNotificationSettings?.pendingTransactionsActive) {
      pushNotifications.push(EPushNotifications.PENDING_TRANSACTIONS);
    }
    if (pushNotificationSettings?.todayTransactionsActive) {
      pushNotifications.push(EPushNotifications.TODAY_TRANSACTIONS);
    }
    if (pushNotificationSettings?.upcomingTransactionsActive) {
      pushNotifications.push(EPushNotifications.UPCOMING_TRANSACTIONS);
    }

    return pushNotifications;
  }, [pushNotificationSettings]);

  const hasChanges = useMemo(() => {
    return objectHasChanges({
      original: {
        notifications: settings?.notifications?.pushNotifications || [],
      },
      edited: {
        notifications: selectedNotifications,
      },
    });
  }, [selectedNotifications, settings]);

  return (
    <Modal
      onEscape={() => {
        if (!isUpdatingSettings) {
          onClose?.();
        }
      }}
      className={clsx(["md:!max-w-[550px]", "md:!w-full"])}
    >
      <Modal.Header
        onClose={() => {
          if (!isUpdatingSettings) {
            onClose?.();
          }
        }}
        title={t("modals:notifications.push.modal_title")}
      />
      <Modal.Body>
        <div className={clsx(["flex", "w-full", "gap-6", "flex-col"])}>
          {showSubscriptionMessage && <PushSubscription />}

          <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
            <PushNotificationsSettings
              onChange={(data: any) => {
                setPushNotificationSettings(data);
              }}
              columns={2}
            />
            <div className="md:text-sm text-xs hidden">
              <p>
                <MdLightbulb size={ICON_SIZES.XS} className="inline mr-1" />
                {t("modals:notifications.push.help_text")}
              </p>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer
        leftContent={hasChanges ? <ModalHasUnsavedChanges /> : null}
        actions={[
          {
            kind: EButtonKind.SAVE,
            icon: <MdCheck size={ICON_SIZES.SM} />,
            onClick: () => setShowConfirmSaveDialog(true),
            text: t("common:buttons.update"),
            isLoading: isUpdatingSettings,
            disabled: isUpdatingSettings || !hasChanges,
          },
          {
            text: t("common:buttons.close"),
            onClick: () => {
              if (!isUpdatingSettings) {
                onClose?.();
              }
            },
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
            disabled: isUpdatingSettings,
          },
        ]}
      />
    </Modal>
  );
};

export default NotificationSettings;
